import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Loader2,
  Link2,
  MessageSquare,
  Search,
  Sparkles,
  FileText,
  Video,
  Music,
  File,
  Globe,
  Copy,
  Check
} from "lucide-react";
import { useEnvironment } from './EnvironmentProvider';
import { useDesignSystem, cn } from './designSystem';

const ResourceSuggestions = ({ isOpen, onClose, consultationText, consultationContext }) => {
  const { colors, typography, effects, componentStyles, shadows, breakpoints } = useDesignSystem();
  const { API_BASE_URL, userId } = useEnvironment();
  const [suggestions, setSuggestions] = useState([]);
  const [allResources, setAllResources] = useState([]);
  const [selectedResources, setSelectedResources] = useState([]);
  const [generatedMessage, setGeneratedMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [messageLoading, setMessageLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('suggestions');
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isTabletView, setIsTabletView] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);
  const [copied, setCopied] = useState(false);

  // Check screen size
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      setIsMobile(width < breakpoints.mobile);
      setIsTabletView(width >= breakpoints.mobile && width < breakpoints.laptop);
      // Også sjekk for landscape-orientering (bredere enn høy)
      setIsLandscape(width > height && width < breakpoints.laptop);
      setIsSmallScreen(width < 480); // Extra small screens
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, [breakpoints.mobile, breakpoints.laptop]);

  // Initialize data when the dialog opens
  useEffect(() => {
    const initializeData = async () => {
      if (isOpen) {
        setIsLoading(true);
        setError(null);
        try {
          // Fetch all resources
          const resourcesResponse = await axios.get(`${API_BASE_URL}/resources`, {
            headers: {
              'X-User-Id': userId,
              'Content-Type': 'application/json'
            }
          });
          setAllResources(resourcesResponse.data);

          // Generate suggestions if we have consultation text
          if (consultationText) {
            try {
              const suggestionsResponse = await axios.post(
                `${API_BASE_URL}/generate-resource-suggestions`,
                { text: consultationText },
                {
                  headers: {
                    'X-User-Id': userId,
                    'Content-Type': 'application/json'
                  }
                }
              );
              
              if (suggestionsResponse.data.error) {
                setError(suggestionsResponse.data.error);
              } else {
                setSuggestions(suggestionsResponse.data.suggestions || []);
              }
            } catch (error) {
              console.error("Error fetching suggestions:", error);
              setError(error.response?.data?.error || 'Could not fetch resource suggestions');
            }
          }
        } catch (error) {
          console.error("Initialization error:", error);
          setError(error.message || 'Could not fetch resources');
        } finally {
          setIsLoading(false);
        }
      }
    };

    initializeData();
  }, [isOpen, consultationText, API_BASE_URL, userId]);

  // Generate message for selected resources
  const generateMessage = async () => {
    if (selectedResources.length === 0) return;
    setMessageLoading(true);
    setError(null);
    
    try {
      const contextData = consultationContext || '';
      const requestData = {
        context: contextData,
        resource_ids: selectedResources
      };

      const response = await axios.post(
        `${API_BASE_URL}/generate-resource-message`,
        requestData,
        {
          headers: {
            'X-User-Id': userId,
            'Content-Type': 'application/json'
          }
        }
      );

      setGeneratedMessage(response.data.message);
    } catch (error) {
      console.error('Error generating message:', error);
      setError(error.response?.data?.error || 'Could not generate message');
    } finally {
      setMessageLoading(false);
    }
  };

  // Toggle resource selection
  const toggleResource = (resourceId) => {
    setSelectedResources(prev => {
      const newSelection = prev.includes(resourceId)
        ? prev.filter(id => id !== resourceId)
        : [...prev, resourceId];
      return newSelection;
    });
    setGeneratedMessage('');
  };

  // Filter resources based on search term
  const filteredResources = allResources.filter(resource => {
    const searchLower = searchTerm.toLowerCase();
    return (
      resource.title.toLowerCase().includes(searchLower) ||
      resource.description.toLowerCase().includes(searchLower) ||
      resource.tags.some(tag => tag.toLowerCase().includes(searchLower))
    );
  });

  // Memoize suggested resources
  const suggestedResources = React.useMemo(() => {
    return suggestions.filter(Boolean);
  }, [suggestions]);

  // Utility function for generating consistent colors based on text
  const getTagColor = (tag) => {
    let hash = 0;
    for (let i = 0; i < tag.length; i++) {
      hash = tag.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colorOptions = [
      cn(colors.primary.light, colors.primary.text),
      cn(colors.success.light, colors.success.text),
      cn(colors.warning.light, colors.warning.text),
      "bg-purple-50 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300",
      "bg-pink-50 text-pink-700 dark:bg-pink-900/50 dark:text-pink-300",
      "bg-indigo-50 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300",
      "bg-teal-50 text-teal-700 dark:bg-teal-900/50 dark:text-teal-300",
    ];
    
    return colorOptions[Math.abs(hash) % colorOptions.length];
  };

  const getResourceIcon = (type) => {
    switch (type) {
      case 'video':
        return <Video className={componentStyles.button.iconSizes.sm} />;
      case 'article':
        return <FileText className={componentStyles.button.iconSizes.sm} />;
      case 'lydfil':
        return <Music className={componentStyles.button.iconSizes.sm} />;
      case 'pdf':
        return <File className={componentStyles.button.iconSizes.sm} />;
      default:
        return <Globe className={componentStyles.button.iconSizes.sm} />;
    }
  };

  // Resource Card Component - optimized for different screen sizes
  const ResourceCard = ({ resource, similarity = null }) => (
    <Card className={cn(
      componentStyles.card.base,
      componentStyles.card.padding.compact,
      "h-full flex flex-col",
      componentStyles.card.hover
    )}>
      <div className="flex items-start gap-2">
        <Checkbox
          checked={selectedResources.includes(resource.id)}
          onCheckedChange={() => toggleResource(resource.id)}
          className={selectedResources.includes(resource.id) ? "data-[state=checked]:bg-blue-500" : ""}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-1">
            <h4 className={cn(
              typography.cardTitle, 
              effects.truncate,
              (isSmallScreen || isLandscape) ? "text-sm max-w-[80%]" : ""
            )}>
              {resource.title}
            </h4>
            {similarity !== null && !isSmallScreen && (
              <span className={cn(
                componentStyles.badge.base,
                componentStyles.badge.primary,
                isLandscape && "text-xs px-1.5 py-0"
              )}>
                {Math.round(similarity * 100)}%
              </span>
            )}
          </div>
          <p className={cn(
            typography.body,
            colors.neutral.textSecondary,
            "mb-1",
            isSmallScreen ? "text-xs line-clamp-1" : 
            isLandscape ? "text-xs line-clamp-2" : "line-clamp-2"
          )}>
            {resource.description}
          </p>
          {!(isSmallScreen && !isLandscape) && (
            <div className="flex flex-wrap gap-1 mb-1">
              {resource.tags.slice(0, isSmallScreen || isLandscape ? 2 : 3).map(tag => (
                <span 
                  key={tag} 
                  className={cn(
                    getTagColor(tag),
                    componentStyles.badge.base,
                    "whitespace-nowrap",
                    (isSmallScreen || isLandscape) && "text-[10px] px-1.5 py-0"
                  )}
                >
                  {tag}
                </span>
              ))}
              {resource.tags.length > (isSmallScreen || isLandscape ? 2 : 3) && (
                <span className={cn(
                  componentStyles.badge.base,
                  colors.neutral.textSecondary,
                  "whitespace-nowrap bg-gray-100 dark:bg-neutral-800",
                  (isSmallScreen || isLandscape) && "text-[10px] px-1.5 py-0"
                )}>
                  +{resource.tags.length - (isSmallScreen || isLandscape ? 2 : 3)}
                </span>
              )}
            </div>
          )}
          <div className="flex items-center gap-1 mt-1">
            <button
              onClick={() => {
                navigator.clipboard.writeText(resource.url);
              }}
              className={cn(
                "p-1 rounded-md",
                colors.neutral.textSecondary,
                "hover:text-blue-600 hover:bg-blue-50 transition-colors dark:hover:text-blue-400 dark:hover:bg-neutral-800"
              )}
              title="Copy link"
            >
              <Copy className={(isSmallScreen || isLandscape) ? "h-3 w-3" : componentStyles.button.iconSizes.sm} />
            </button>
            <a 
              href={resource.url}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                "p-1 rounded-md",
                colors.primary.text,
                "hover:bg-blue-50 transition-colors dark:hover:bg-neutral-800"
              )}
              title="Open resource"
            >
              <Link2 className={(isSmallScreen || isLandscape) ? "h-3 w-3" : componentStyles.button.iconSizes.sm} />
            </a>
          </div>
        </div>
      </div>
    </Card>
  );

  // Action buttons for mobile view - fixed position at bottom
  const MobileActionButtons = () => (
    <div className={cn(
      "fixed bottom-0 left-0 right-0 z-50 p-3 bg-white dark:bg-neutral-900 border-t",
      colors.neutral.border,
      "flex space-x-2",
      effects.glassmorphismDense,
      isLandscape && "p-2"
    )}>
      <Button
        onClick={generateMessage}
        disabled={selectedResources.length === 0 || messageLoading}
        className={cn(
          componentStyles.button.base,
          componentStyles.button.primary,
          "flex-1",
          isLandscape && "py-1 text-xs h-8",
          selectedResources.length === 0 && componentStyles.button.states.disabled,
          messageLoading && componentStyles.button.states.loading
        )}
      >
        {messageLoading ? (
          <>
            <Loader2 className={cn(
              "mr-2 h-4 w-4",
              isLandscape && "h-3 w-3",
              effects.spin
            )} />
            Genererer...
          </>
        ) : (
          <>
            <MessageSquare className={cn(
              "mr-2 h-4 w-4",
              isLandscape && "h-3 w-3"
            )} />
            Generer melding
          </>
        )}
      </Button>
      
      {generatedMessage && (
        <Button
          onClick={() => {
            navigator.clipboard.writeText(generatedMessage);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
          variant="outline"
          className={cn(
            componentStyles.button.base,
            componentStyles.button.secondary,
            isLandscape && "py-1 text-xs h-8"
          )}
        >
          {copied ? (
            <Check className={cn(
              "h-4 w-4 text-green-500",
              isLandscape && "h-3 w-3"
            )} />
          ) : (
            <Copy className={cn(
              "h-4 w-4",
              isLandscape && "h-3 w-3"
            )} />
          )}
        </Button>
      )}
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose} className="max-w-full">
      <DialogContent className={cn(
        componentStyles.dialog.base,
        "max-w-5xl max-h-[90vh] flex flex-col overflow-hidden",
        isMobile && !isLandscape && "w-[calc(100vw-24px)] mx-auto", 
        isTabletView && "w-[calc(100vw-48px)] max-h-[85vh] mx-auto",
        isLandscape && isMobile && "max-h-[85vh] h-[calc(100vh-40px)] w-[calc(100vw-24px)] mx-auto"
      )}>
        <DialogHeader className={cn(
          "pb-2",
          isSmallScreen && "px-3 py-2",
          isLandscape && "px-3 py-1"
        )}>
          <DialogTitle className={cn(
            colors.neutral.textPrimary, 
            (isSmallScreen || isLandscape) && "text-base"
          )}>
            Ressursforslag
          </DialogTitle>
          {!(isSmallScreen || isLandscape) && (
            <DialogDescription className={colors.neutral.textSecondary}>
              Basert på konsultasjonsinnholdet har vi funnet disse ressursene som kan være relevante.
            </DialogDescription>
          )}
        </DialogHeader>

        {/* For very small screens, use a completely different layout */}
        {isSmallScreen ? (
          <div className="flex-1 flex flex-col overflow-hidden">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
              <TabsList className={cn(
                colors.neutral.bg,
                "dark:bg-neutral-900 px-1"
              )}>
                <TabsTrigger 
                  value="suggestions" 
                  className={cn(
                    "flex items-center gap-1 text-xs py-1 px-2",
                    "data-[state=active]:bg-blue-50 dark:data-[state=active]:bg-neutral-800"
                  )}
                >
                  <Sparkles className="h-3 w-3" />
                  <span>Forslag ({suggestedResources.length})</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="search" 
                  className={cn(
                    "flex items-center gap-1 text-xs py-1 px-2",
                    "data-[state=active]:bg-blue-50 dark:data-[state=active]:bg-neutral-800"
                  )}
                >
                  <Search className="h-3 w-3" />
                  <span>Søk</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="message" 
                  className={cn(
                    "flex items-center gap-1 text-xs py-1 px-2",
                    "data-[state=active]:bg-blue-50 dark:data-[state=active]:bg-neutral-800"
                  )}
                >
                  <MessageSquare className="h-3 w-3" />
                  <span>Melding</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="suggestions" className="flex-1 mt-2 px-2 overflow-hidden">
                <div className="text-center mb-2">
                  <p className={cn(
                    typography.small,
                    colors.neutral.textSecondary,
                    "italic"
                  )}>
                    {selectedResources.length} ressurser valgt
                  </p>
                </div>
                
                <ScrollArea className="h-[55vh] pr-2">
                  {isLoading ? (
                    <div className="flex items-center justify-center h-24">
                      <div className="flex flex-col items-center space-y-2">
                        <Loader2 className={cn(
                          "h-6 w-6",
                          colors.primary.text,
                          effects.spin
                        )} />
                        <p className={cn(
                          typography.small,
                          colors.neutral.textSecondary
                        )}>
                          Laster forslag...
                        </p>
                      </div>
                    </div>
                  ) : error ? (
                    <div className={cn(
                      typography.center,
                      typography.error,
                      "py-4"
                    )}>
                      <p className="text-sm">{error}</p>
                    </div>
                  ) : suggestedResources.length === 0 ? (
                    <div className={cn(
                      typography.center,
                      colors.neutral.textTertiary,
                      "py-4"
                    )}>
                      <p className="text-sm">Ingen foreslåtte ressurser funnet</p>
                      <p className={cn(
                        typography.small,
                        "mt-1"
                      )}>
                        Prøv søk-fanen for å finne ressurser
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-2">
                      {suggestedResources.map((resource) => (
                        <ResourceCard
                          key={resource.id}
                          resource={resource}
                          similarity={resource.similarity_score}
                        />
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>

              <TabsContent value="search" className="flex-1 mt-2 px-2 overflow-hidden">
                <div className="mb-2">
                  <Input
                    placeholder="Søk i ressurser..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={cn(
                      colors.neutral.bg,
                      colors.neutral.textPrimary,
                      "dark:bg-neutral-900 dark:border-neutral-700",
                      "text-sm h-8"
                    )}
                  />
                </div>
                <ScrollArea className="h-[52vh] pr-2">
                  <div className="grid grid-cols-1 gap-2">
                    {filteredResources.map((resource) => (
                      <ResourceCard key={resource.id} resource={resource} />
                    ))}
                    
                    {filteredResources.length === 0 && (
                      <div className={cn(
                        typography.center,
                        colors.neutral.textTertiary,
                        "py-4"
                      )}>
                        Ingen ressurser matcher søket
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </TabsContent>

              <TabsContent value="message" className="flex-1 mt-2 px-2 overflow-hidden">
                <ScrollArea className="h-[50vh]">
                  {generatedMessage ? (
                    <div className="prose prose-sm max-w-none">
                      <p className={cn(
                        "whitespace-pre-wrap",
                        colors.neutral.textPrimary,
                        "text-sm"
                      )}>
                        {generatedMessage}
                      </p>
                    </div>
                  ) : (
                    <div className={cn(
                      typography.center,
                      typography.placeholder,
                      "py-8 italic"
                    )}>
                      <p className="text-sm">
                        {selectedResources.length === 0 
                          ? "Velg ressurser for å generere en melding" 
                          : "Klikk på 'Generer melding' nedenfor"}
                      </p>
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
            </Tabs>
            
            {/* Fixed action buttons at bottom */}
            <MobileActionButtons />
          </div>
        ) : (
          // Original layout for tablets and desktop with improvements
          <div className={cn(
            "flex flex-col lg:flex-row gap-4 flex-1 overflow-hidden",
            isMobile ? "space-y-4" : isTabletView ? "flex-col space-y-3" : "space-x-4"
          )}>
            <div className={cn(
              "flex flex-col",
              isMobile ? "flex-1" : 
              isTabletView ? "flex-[3]" : "w-2/3"
            )}>
              <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
                <TabsList className={cn(
                  colors.neutral.bg,
                  "dark:bg-neutral-900",
                  isLandscape && "p-0.5"
                )}>
                  <TabsTrigger 
                    value="suggestions" 
                    className={cn(
                      "flex items-center gap-2",
                      "data-[state=active]:bg-blue-50 dark:data-[state=active]:bg-neutral-800",
                      isLandscape && "text-xs py-1 px-2"
                    )}
                  >
                    <Sparkles className={cn(
                      componentStyles.button.iconSizes.sm,
                      isLandscape && "h-3 w-3"
                    )} />
                    Forslag ({suggestedResources.length})
                  </TabsTrigger>
                  <TabsTrigger 
                    value="search" 
                    className={cn(
                      "flex items-center gap-2",
                      "data-[state=active]:bg-blue-50 dark:data-[state=active]:bg-neutral-800",
                      isLandscape && "text-xs py-1 px-2"
                    )}
                  >
                    <Search className={cn(
                      componentStyles.button.iconSizes.sm,
                      isLandscape && "h-3 w-3"
                    )} />
                    Søk i alle ressurser
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="suggestions" className="flex-1 mt-3">
                  <ScrollArea className={cn(
                    "h-auto",
                    isMobile && !isLandscape && "h-[35vh]",
                    isTabletView && !isLandscape && "h-[45vh]", // Increased height for tablets
                    isLandscape && "h-[25vh]",
                    "pr-2"
                  )}>
                    {isLoading ? (
                      <div className="flex items-center justify-center h-24">
                        <div className="flex flex-col items-center space-y-2">
                          <Loader2 className={cn(
                            "h-8 w-8",
                            colors.primary.text,
                            effects.spin,
                            isLandscape && "h-6 w-6"
                          )} />
                          <p className={cn(
                            typography.body,
                            colors.neutral.textSecondary,
                            isLandscape && "text-xs"
                          )}>
                            Laster forslag...
                          </p>
                        </div>
                      </div>
                    ) : error ? (
                      <div className={cn(
                        typography.center,
                        typography.error,
                        "py-8"
                      )}>
                        <p>{error}</p>
                      </div>
                    ) : suggestedResources.length === 0 ? (
                      <div className={cn(
                        typography.center,
                        colors.neutral.textTertiary,
                        "py-8"
                      )}>
                        <p>Ingen foreslåtte ressurser funnet</p>
                        <p className={cn(
                          typography.small,
                          "mt-2"
                        )}>
                          Prøv å søke etter ressurser i "Søk"-fanen
                        </p>
                      </div>
                    ) : (
                      <div className={cn(
                        "grid gap-3 pr-2", // Reduced gap for tighter layout
                        isLandscape ? "grid-cols-2 gap-2" :
                        isMobile ? "grid-cols-1" :
                        isTabletView ? "grid-cols-2" : // Always 2 columns for tablet
                        "grid-cols-2"
                      )}>
                        {suggestedResources.map((resource) => (
                          <ResourceCard
                            key={resource.id}
                            resource={resource}
                            similarity={resource.similarity_score}
                          />
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </TabsContent>

                <TabsContent value="search" className="flex-1 mt-3">
                  <div className="mb-3">
                    <Input
                      placeholder="Søk i ressurser..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className={cn(
                        colors.neutral.bg,
                        colors.neutral.textPrimary,
                        "dark:bg-neutral-900 dark:border-neutral-700",
                        isLandscape && "text-sm h-8"
                      )}
                    />
                  </div>
                  <ScrollArea className={cn(
                    "h-auto",
                    isMobile && !isLandscape && "h-[30vh]",
                    isTabletView && !isLandscape && "h-[42vh]", // Increased height for tablets
                    isLandscape && "h-[22vh]",
                    "pr-2"
                  )}>
                    <div className={cn(
                      "grid gap-3 pr-2", // Reduced gap for a tighter layout
                      isLandscape ? "grid-cols-2 gap-2" :
                      isMobile ? "grid-cols-1" :
                      isTabletView ? "grid-cols-2" : // Always 2 columns for tablet
                      "grid-cols-2"
                    )}>
                      {filteredResources.map((resource) => (
                        <ResourceCard key={resource.id} resource={resource} />
                      ))}
                      
                      {filteredResources.length === 0 && (
                        <div className={cn(
                          "col-span-full",
                          typography.center,
                          colors.neutral.textTertiary,
                          "py-8"
                        )}>
                          Ingen ressurser matcher søket
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </TabsContent>
              </Tabs>
            </div>

            <div className={cn(
              colors.neutral.bgSecondary,
              colors.neutral.border,
              "rounded-lg p-4 flex flex-col",
              isMobile ? "flex-1" : 
              isTabletView ? "flex-[2]" : "w-1/3", // Adjusted ratio for tablets
              isLandscape && "p-3" // Reduced padding in landscape
            )}>
              <div className={cn(
                "mb-3", 
                isLandscape && "mb-2"
              )}>
                <h3 className={cn(
                  typography.h3,
                  colors.neutral.textPrimary,
                  isLandscape && "text-base"
                )}>
                  Melding til klient
                </h3>
                <p className={cn(
                  typography.small,
                  colors.neutral.textSecondary
                )}>
                  {selectedResources.length} ressurser valgt
                </p>
              </div>
              
              <ScrollArea className={cn(
                "flex-1 mb-3",
                isMobile && "h-[20vh]",
                isTabletView && !isLandscape && "h-[20vh]", // Adjusted height for better balance
                isLandscape && "h-[10vh] mb-2"
              )}>
                {generatedMessage ? (
                  <div className="prose prose-sm max-w-none">
                    <p className={cn(
                      "whitespace-pre-wrap",
                      colors.neutral.textPrimary,
                      isLandscape && "text-xs" // Smaller text in landscape
                    )}>
                      {generatedMessage}
                    </p>
                  </div>
                ) : (
                  <div className={cn(
                    typography.center,
                    typography.placeholder,
                    isLandscape ? "py-4 text-xs" : "py-8 italic"
                  )}>
                    Velg ressurser og generer en personlig melding til klienten
                  </div>
                )}
              </ScrollArea>
              
              <div className={cn(
                "space-y-2 mt-auto",
                isTabletView && "flex gap-2 space-y-0",
                isLandscape && "space-y-1"
              )}>
                <Button
                  onClick={generateMessage}
                  disabled={selectedResources.length === 0 || messageLoading}
                  className={cn(
                    componentStyles.button.base,
                    componentStyles.button.primary,
                    isTabletView ? "flex-1" : "w-full",
                    isLandscape && "h-8 text-xs py-1",
                    selectedResources.length === 0 && componentStyles.button.states.disabled,
                    messageLoading && componentStyles.button.states.loading
                  )}
                >
                  {messageLoading ? (
                    <>
                      <Loader2 className={cn(
                        "mr-2 h-4 w-4",
                        effects.spin,
                        isLandscape && "h-3 w-3"
                      )} />
                      Genererer...
                    </>
                  ) : (
                    <>
                      <MessageSquare className={cn(
                        "mr-2 h-4 w-4",
                        isLandscape && "h-3 w-3"
                      )} />
                      Generer melding
                    </>
                  )}
                </Button>
                
                {generatedMessage && (
                  <Button
                    onClick={() => {
                      navigator.clipboard.writeText(generatedMessage);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                    variant="outline"
                    className={cn(
                      componentStyles.button.base,
                      componentStyles.button.secondary,
                      isTabletView ? "flex-1" : "w-full",
                      isLandscape && "h-8 text-xs py-1"
                    )}
                  >
                    {copied ? (
                      <>
                        <Check className={cn(
                          "mr-2 h-4 w-4 text-green-500",
                          isLandscape && "h-3 w-3"
                        )} />
                        Kopiert!
                      </>
                    ) : (
                      <>
                        <Copy className={cn(
                          "mr-2 h-4 w-4",
                          isLandscape && "h-3 w-3"
                        )} />
                        {isLandscape ? "Kopier" : "Kopier til utklippstavle"}
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ResourceSuggestions;